'use client';

import { useState } from 'react';
import { Container, Paper, Box, Button, Alert, Snackbar } from '@mui/material';
import { useRouter } from 'next/navigation';
import ProgressBar from './components/ProgressBar';
import DisciplineStep from './Steps/DisciplineStep';
import CategoryStep from './Steps/CategoryStep';
import RequirementsStep from './Steps/RequirementsStep';
import ModelUploadStep from './Steps/ModelUploadStep';
import JobDetailsStep from './Steps/JobDetailsStep';
import ReviewStep from './Steps/ReviewStep';

const STEP_TITLES = [
  'Select Discipline',
  'Choose Categories',
  'Set Requirements',
  'Upload Model',
  'Job Details',
  'Review',
];

interface FormState {
  discipline: string | null;
  categories: string[];
  requirements: {
    [key: string]: {
      must: boolean;
      preferred: boolean;
    };
  };
  modelUrl?: string;
  modelFile?: File;
  jobDetails: {
    title: string;
    description: string;
    budget?: number;
    timeline?: string;
  };
}

const initialFormState: FormState = {
  discipline: null,
  categories: [],
  requirements: {},
  jobDetails: {
    title: '',
    description: '',
  },
};

export default function JobPostingForm() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDisciplineSelect = (discipline: string) => {
    setFormState(prev => ({
      ...prev,
      discipline,
      categories: [], // Reset categories when discipline changes
      requirements: {}, // Reset requirements when discipline changes
    }));
  };

  const handleCategorySelect = (categoryId: string) => {
    setFormState(prev => {
      const categories = prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId];

      // Remove requirements for deselected categories
      const requirements = { ...prev.requirements };
      if (!categories.includes(categoryId)) {
        Object.keys(requirements).forEach(key => {
          if (key.startsWith(categoryId)) {
            delete requirements[key];
          }
        });
      }

      return {
        ...prev,
        categories,
        requirements,
      };
    });
  };

  const handleRequirementChange = (
    categoryId: string,
    value: { must: boolean; preferred: boolean }
  ) => {
    setFormState(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [categoryId]: value,
      },
    }));
  };

  const handleModelUpload = (file: File) => {
    // Create a temporary URL for the uploaded file
    const objectUrl = URL.createObjectURL(file);
    setFormState(prev => ({
      ...prev,
      modelUrl: objectUrl,
      modelFile: file,
    }));
  };

  const handleJobDetailsChange = (field: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      jobDetails: {
        ...prev.jobDetails,
        [field]: value,
      },
    }));
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, STEP_TITLES.length));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleEditSection = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // First, upload the model file if present
      let fileUrl = null;
      if (formState.modelFile) {
        const formData = new FormData();
        formData.append('file', formState.modelFile);

        const uploadResponse = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const uploadError = await uploadResponse.json();
          throw new Error(uploadError.error || 'Failed to upload model file');
        }

        const { url } = await uploadResponse.json();
        fileUrl = url;
      }

      // Then create the job with the file URL
      const response = await fetch('/api/jobs/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discipline: formState.discipline,
          categories: formState.categories,
          requirements: formState.requirements,
          jobDetails: formState.jobDetails,
          file: fileUrl
            ? {
                url: fileUrl,
                name: formState.modelFile?.name || 'model.stl',
                size: formState.modelFile?.size || 0,
                type: 'STL',
              }
            : undefined,
        }),
      });

      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        throw new Error('Server response was not JSON');
      }

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to create job');
      }

      // Redirect to the job detail page
      router.push(`/jobs/${responseData.id}`);
    } catch (err) {
      console.error('Error submitting job:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit job');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!formState.discipline;
      case 2:
        return formState.categories.length > 0;
      case 3: {
        // Require at least one must-have or preferred requirement per category
        return formState.categories.every(categoryId => {
          const reqs = Object.entries(formState.requirements).filter(([key]) =>
            key.startsWith(categoryId)
          );
          return reqs.some(([, value]) => value.must || value.preferred);
        });
      }
      case 4:
        // Model upload is optional
        return true;
      case 5:
        // Only title and description are required
        return !!formState.jobDetails.title.trim() && !!formState.jobDetails.description.trim();
      case 6:
        // Review step - can always proceed to submit
        return true;
      default:
        return true;
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 4, my: 4 }}>
        <ProgressBar
          currentStep={currentStep}
          totalSteps={STEP_TITLES.length}
          stepTitles={STEP_TITLES}
        />

        <Box sx={{ minHeight: 400 }}>
          {currentStep === 1 && (
            <DisciplineStep
              selectedDiscipline={formState.discipline}
              onSelect={handleDisciplineSelect}
            />
          )}
          {currentStep === 2 && formState.discipline && (
            <CategoryStep
              discipline={formState.discipline}
              selectedCategories={formState.categories}
              onSelectCategory={handleCategorySelect}
            />
          )}
          {currentStep === 3 && formState.discipline && (
            <RequirementsStep
              discipline={formState.discipline}
              selectedCategories={formState.categories}
              requirements={formState.requirements}
              onRequirementChange={handleRequirementChange}
            />
          )}
          {currentStep === 4 && (
            <ModelUploadStep modelUrl={formState.modelUrl} onModelUpload={handleModelUpload} />
          )}
          {currentStep === 5 && (
            <JobDetailsStep details={formState.jobDetails} onChange={handleJobDetailsChange} />
          )}
          {currentStep === 6 && (
            <ReviewStep formData={formState} onEditSection={handleEditSection} />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" onClick={handleBack} disabled={currentStep === 1}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={currentStep === STEP_TITLES.length ? handleSubmit : handleNext}
            disabled={!canProceed() || isSubmitting}
          >
            {currentStep === STEP_TITLES.length
              ? isSubmitting
                ? 'Submitting...'
                : 'Submit Job'
              : 'Next'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
